import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  BookmarkIcon,
  CheckIcon,
  DownloadIcon,
  FileTextIcon,
  PencilIcon,
  PinIcon,
  ReplyIcon,
  SettingsIcon,
  TargetIcon,
  Trash2Icon,
  UserPlusIcon,
  UserXIcon,
} from "lucide-react";

import { LabelChooser } from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";
import { Button } from "simplydo/react/ui/button";
import {
  Dropdown,
  DropdownDivider,
  DropdownItem,
  DropdownItemLink,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { Icon } from "simplydo/react/ui/icon";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import IdeaPdfExport from "components/lib/IdeaPdfExport";
import type { IdeaPreviewOwnerChallenge } from "components/lib/Ideas/IdeaPreview/types";

import { useIdea } from "./hooks/useIdea";

type SubmittedActionsDropdownProps = {
  isEditing: boolean;
  setEditing: (isEditing: boolean) => void;
  onSubmit: (isSubmitted: boolean) => void;
  updateIdea: (data: unknown, path: string[], replace?: boolean) => void;
  deleteIdea: () => void;
  leaveIdea: () => void;
};

const SubmittedActionsDropdown = ({
  isEditing,
  setEditing,
  onSubmit,
  updateIdea,
  deleteIdea,
  leaveIdea,
}: SubmittedActionsDropdownProps) => {
  const [idea] = useIdea();
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [labelChooserOpen, setLabelChooserOpen] = useState(false);
  const [isUnsubmitting, setIsUnsubmitting] = useState(false);

  const ideaId = idea?._id;
  const userId = user?._id;
  const canManageChallenge = util.canManageChallenge(
    user!,
    idea?.ownerChallenge as IdeaPreviewOwnerChallenge | undefined,
  );
  const canEditIdeas =
    util.hasPermission(user, "challenge.editIdeas", idea?.challenge) ||
    util.hasPermission(
      user,
      "org.manageChallenges",
      idea?.ownerChallenge?.organisation,
    );
  const canEditIdea = util.canEditIdea(user, idea);
  const canUpdateIdeaStatus = util.hasPermission(
    user,
    "challenge.updateIdeaStatus",
    idea?.challenge,
  );
  const canUnsubmit = canEditIdea || canManageChallenge || canUpdateIdeaStatus;

  const joinIdea = useCallback(() => {
    if (!ideaId || !userId) return;
    api.ideas.addCollaborator(
      ideaId,
      userId,
      ({ authors, collaborators }) => {
        updateIdea(authors, ["authors"], true);
        updateIdea(collaborators, ["collaborators"], true);
      },
      (err) => toast.error(err.message),
    );
  }, [ideaId, userId, updateIdea]);

  const pinIdea = useCallback(() => {
    if (!ideaId) return;
    api.ideas.updateStatus(
      ideaId,
      { isPinned: !idea?.isPinned },
      (ideaStatus) => {
        updateIdea(ideaStatus.isPinned, ["isPinned"], true);
      },
      (err) => toast.error(err.message),
    );
  }, [ideaId, idea?.isPinned, updateIdea]);

  const labelIdea = useCallback(
    (label: string, isApplied: boolean) => {
      if (!ideaId) return;
      let labels = idea?.labels || [];
      if (labels.includes(label)) {
        if (!isApplied) {
          labels = labels.filter((l) => l !== label);
        }
      } else if (isApplied) {
        labels = [...labels, label];
      }

      api.ideas.updateStatus(
        ideaId,
        { labels },
        () => {
          updateIdea(labels, ["labels"], true);
        },
        (err) => toast.error(err.message),
      );
    },
    [ideaId, idea?.labels, updateIdea],
  );

  const createChallengeFromIdea = useCallback(() => {
    if (!ideaId) return;
    api.challenges.createFromIdea(
      { idea: ideaId },
      (data) => {
        navigate(`/challenges/${data.challenge._id}`);
        toast.success("Challenge created");
      },
      (err) => toast.error(err.message),
    );
  }, [ideaId, navigate]);

  const unsubmitIdea = useCallback(() => {
    if (!ideaId) return;
    util
      .confirm(
        t("ideas.submit.confirm.withdraw.title"),
        t("ideas.submit.confirm.withdraw.info"),
      )
      .then(() => {
        setIsUnsubmitting(true);
        api.ideas.updateStatus(
          ideaId,
          { isSubmitted: false },
          (newIdea) => {
            util.onUpdateIdea(newIdea, newIdea);
            toast.success(t("ideas.submit.confirm.unsubmitSuccess"));
            onSubmit(false);
            setIsUnsubmitting(false);
            api.invalidateQueries(api.challenges.getIdeas, [newIdea.challenge]);
          },
          (err) => {
            if (err.status === 400) {
              updateIdea("closed", ["ownerChallenge", "stage"], true);
            }
            toast.error(err.message);
            setIsUnsubmitting(false);
          },
        );
      })
      .catch(() => {});
  }, [ideaId, t, onSubmit, updateIdea]);

  if (!idea) {
    return null;
  }

  return (
    <>
      <LabelChooser
        isOpen={labelChooserOpen}
        onClose={() => setLabelChooserOpen(false)}
        onComplete={(em) => labelIdea(em, true)}
        existing={idea?.labels}
        onRemoveExisting={(em) => labelIdea(em, false)}
      />
      <Dropdown
        direction="left"
        trigger={
          <Button size="small" color="secondary">
            <Icon icon={SettingsIcon} />
          </Button>
        }
      >
        <DropdownMenu>
          {canEditIdeas ? (
            <DropdownItem onClick={() => setEditing(!isEditing)}>
              <Icon icon={isEditing ? CheckIcon : PencilIcon} />
              {isEditing ? "Done editing" : `Edit ${t("generic.idea")}`}
            </DropdownItem>
          ) : null}

          {canUnsubmit ? (
            <DropdownItem onClick={unsubmitIdea} disabled={isUnsubmitting}>
              <Icon
                icon={
                  canEditIdeas ||
                  util.hasPermission(
                    user,
                    "org.manageChallenges",
                    idea?.ownerChallenge?.organisation,
                  )
                    ? ReplyIcon
                    : PencilIcon
                }
              />
              {canEditIdea
                ? `Unsubmit ${t("generic.idea")}`
                : t("generic.unsubmit")}
            </DropdownItem>
          ) : null}

          {canEditIdeas || canUnsubmit ? <DropdownDivider /> : null}

          <IdeaPdfExport
            hidePopup
            ideas={[idea]}
            challenge={idea?.ownerChallenge}
            trigger={
              <DropdownItem>
                <Icon icon={FileTextIcon} />
                {t("ideas.options.download.pdf")}
              </DropdownItem>
            }
          />

          {canManageChallenge ||
          util.hasPermission(user!, "challenge.pinIdeas", idea?.challenge) ? (
            <DropdownItem onClick={pinIdea}>
              <Icon icon={PinIcon} />
              {idea?.isPinned
                ? t("ideas.options.pin.unPin")
                : t("ideas.options.pin.challenge")}
            </DropdownItem>
          ) : null}

          {user && idea?.user !== user._id ? (
            <DropdownItem onClick={canEditIdea ? leaveIdea : joinIdea}>
              <Icon icon={canEditIdea ? UserXIcon : UserPlusIcon} />
              {canEditIdea
                ? `Leave ${t("generic.idea")}`
                : `Collaborate on this ${t("generic.idea")}`}
            </DropdownItem>
          ) : null}

          {util.hasPermission(
            user!,
            "challenge.moveIdeaToChallenge",
            idea?.challenge,
          ) && idea?.challengeCreation ? (
            <DropdownItem onClick={() => createChallengeFromIdea()}>
              <Icon icon={TargetIcon} />
              Create {t("generic.challengeWithArticle")} from this{" "}
              {t("generic.idea")}
            </DropdownItem>
          ) : null}

          {!idea?.ownerChallenge?.preventIdeaCoverImages && idea?.coverImage ? (
            <DropdownItemLink
              href={idea?.coverImageUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon icon={DownloadIcon} />
              {t("ideas.download.coverImage")}
            </DropdownItemLink>
          ) : null}

          {util.hasPermission(
            user!,
            "challenge.editIdeaLabels",
            idea?.challenge,
          ) ? (
            <DropdownItem
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setLabelChooserOpen(true);
              }}
            >
              <Icon icon={BookmarkIcon} />
              Apply a label
            </DropdownItem>
          ) : null}

          {canEditIdea ||
          util.hasPermission(
            user!,
            "org.managePermissions",
            user!.ownerOrganisation?._id,
          ) ? (
            <>
              <DropdownDivider />
              <DropdownItem onClick={() => deleteIdea()}>
                <Icon icon={Trash2Icon} />
                {`Delete ${t("generic.idea")}`}
              </DropdownItem>
            </>
          ) : null}
        </DropdownMenu>
      </Dropdown>
    </>
  );
};

export default SubmittedActionsDropdown;
